import { Grid, GridProps, Typography } from '@mui/material';
import { ScrollableGridContainerContextProvider } from 'components/Providers/ContextProviders';
import { UNIQUE_SELECTOR_ID } from 'constants/index';
import React from 'react';

type SparkScrollableContainerProps = {
  children: React.ReactNode;
  header?: React.ReactNode;
  subHeader?: string;
  headerClasses?: string;
  divider?: boolean;
  actions?: React.ReactNode;
  scrollableContainerStyle?: GridProps['sx'];
  headerStyle?: GridProps['sx'];
  actionContainerStyles?: GridProps['sx'];
  height?: React.CSSProperties['height'];
  floatingAction?: boolean;
} & GridProps;

function SparkScrollableGridContainer({
  children,
  header,
  subHeader,
  divider = true,
  actions,
  height,
  headerClasses = '',
  scrollableContainerStyle = {},
  headerStyle = {},
  actionContainerStyles = {},
  floatingAction = false,
  ...gridProps
}: SparkScrollableContainerProps) {
  const cardID = React.useMemo(
    () => `sparkForm-scrollable-card-id-${crypto.randomUUID()}`,
    []
  );
  const [calculatedHeight, setCalculatedHeight] =
    React.useState<React.CSSProperties['height']>(0);

  React.useEffect(() => {
    const currentElement = document.getElementById(cardID);
    if (!currentElement?.parentElement) return;

    const parentElement = currentElement.parentElement;

    const updateHeight = () => {
      if (!currentElement || !parentElement) return;

      const topOffset = currentElement.getBoundingClientRect().top;
      const parentHeight = parentElement.clientHeight;
      const parentTopOffset = parentElement.getBoundingClientRect().top;
      const topDifferential = topOffset - parentTopOffset;

      if (parentHeight > 0) {
        setCalculatedHeight(
          `min(calc(100vh - ${topOffset}px), calc(${parentHeight}px - ${topDifferential}px))`
        );
      }
    };

    // Resize Observer for both currentElement and parent
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(parentElement);
    resizeObserver.observe(currentElement);

    updateHeight();

    return () => {
      resizeObserver.disconnect();
    };
  }, [cardID]);

  return (
    <ScrollableGridContainerContextProvider
      initialValues={{
        scrollableGridContainer: { height: height || calculatedHeight },
        scrollableChildContainer: { height: height || calculatedHeight },
      }}
    >
      <Grid
        id={cardID}
        container
        direction='column'
        overflow='hidden'
        item
        height={height || calculatedHeight}
        position='relative'
        {...gridProps}
      >
        {!!header && (
          <Grid
            container
            item
            position='sticky'
            top={0}
            height='4rem'
            borderBottom={(theme) => `1px solid ${theme.palette.borderColor}`}
            alignItems='center'
            justifyContent='space-between'
            paddingX={2}
            className={headerClasses}
            sx={{ ...headerStyle }}
          >
            {header}
            {!!subHeader && (
              <Typography
                fontFamily='Mont-SemiBold'
                fontSize={16}
                color={(theme) => theme.palette.grey[400]}
              >
                {subHeader}
              </Typography>
            )}
          </Grid>
        )}

        <Grid
          item
          container
          className={UNIQUE_SELECTOR_ID.SPARK_SCROLLABLE_CHILD_CONTAINER_CLASS}
          flex={1}
          paddingX={2}
          paddingTop={2}
          alignContent='flex-start'
          component='div'
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            ...scrollableContainerStyle,
          }}
        >
          {children}
        </Grid>

        {!!actions && (
          <Grid
            item
            paddingY='18px'
            paddingX={2}
            borderRadius={0}
            position={floatingAction ? 'absolute' : 'unset'}
            bottom={floatingAction ? 0 : 'unset'}
            left={floatingAction ? 0 : 'unset'}
            right={floatingAction ? 0 : 'unset'}
            sx={actionContainerStyles}
            width={'100%'}
          >
            {actions}
          </Grid>
        )}
      </Grid>
    </ScrollableGridContainerContextProvider>
  );
}

export default SparkScrollableGridContainer;
